import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { CheckCircle2, XCircle, Info, Clock, RefreshCcw, X as XIcon, AlertTriangle } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Modal showing structured fare conditions (refundable, change/cancel/no-show penalties)
 * extracted from each supplier's offer:
 *  - Travelport: parsed from <air:AirPricingInfo> + ChangePenalty + CancelPenalty
 *  - Amadeus self-service: pricingOptions.refundableFare + lastTicketingDate only
 *    (detailed penalty amounts require a separate detailed-fare-rules call)
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (next:boolean) => void
 *  - flight: full flight object (must carry .fare_rules)
 *  - segmentLabel: e.g. "Segment 1 · CAI → DXB" used in the title
 */
const fmtMoney = (amount, cur = "USD") => {
  if (amount == null) return "—";
  const n = Number(amount) || 0;
  if (n === 0) return "Free";
  return `${cur} ${n.toFixed(2)}`;
};

const penaltyLabel = (p) => {
  if (!p) return "Per airline fare rules";
  const parts = [];
  if (p.amount) parts.push(fmtMoney(p.amount, p.currency || "USD"));
  if (p.percentage != null) parts.push(`${p.percentage}%`);
  return parts.length ? parts.join(" + ") : "Per airline fare rules";
};

export default function FareRulesDialog({ open, onOpenChange, flight, segmentLabel }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const fr = flight?.fare_rules || {};
  const supplier = flight?.supplier_source || flight?.source || "amadeus";
  const isAmadeus = supplier === "amadeus";
  const isTravelport = supplier === "travelport";
  const refundable = fr.refundable;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="fare-rules-dialog">
        <DialogHeader>
          <DialogTitle className="text-[var(--brand-accent)] font-heading flex items-center gap-2">
            <Info className="w-4 h-4 text-[var(--brand-primary)]" /> Fare rules
          </DialogTitle>
          <DialogDescription className="text-xs">
            {segmentLabel} · {flight?.airline_name || flight?.airline_code} {flight?.flight_number}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          {/* Refundable */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-testid="fare-rule-refundable">
            <div className="flex items-center gap-2 text-sm font-medium text-[var(--brand-accent)]">
              <RefreshCcw className="w-4 h-4 text-[var(--brand-primary)]" /> Refundable
            </div>
            {refundable === true ? (
              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1" /> Yes</Badge>
            ) : refundable === false ? (
              <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200"><XCircle className="w-3 h-3 mr-1" /> No</Badge>
            ) : (
              <Badge variant="outline" className="bg-slate-100 text-slate-600">Unknown</Badge>
            )}
          </div>

          {/* Change penalty */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-testid="fare-rule-change">
            <div className="flex items-center gap-2 text-sm font-medium text-[var(--brand-accent)]">
              <RefreshCcw className="w-4 h-4 text-[var(--brand-primary)]" /> Change fee
            </div>
            <span className="text-sm text-slate-700">{penaltyLabel(fr.change)}</span>
          </div>

          {/* Cancel penalty */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-testid="fare-rule-cancel">
            <div className="flex items-center gap-2 text-sm font-medium text-[var(--brand-accent)]">
              <XIcon className="w-4 h-4 text-[var(--brand-primary)]" /> Cancellation fee
            </div>
            <span className="text-sm text-slate-700">{penaltyLabel(fr.cancel)}</span>
          </div>

          {/* No-show penalty */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-testid="fare-rule-noshow">
            <div className="flex items-center gap-2 text-sm font-medium text-[var(--brand-accent)]">
              <AlertTriangle className="w-4 h-4 text-[var(--brand-primary)]" /> No-show fee
            </div>
            <span className="text-sm text-slate-700">{penaltyLabel(fr.no_show)}</span>
          </div>

          {/* Latest ticketing */}
          {fr.latest_ticketing && (
            <div className="flex items-center justify-between p-3 rounded-xl bg-amber-50" data-testid="fare-rule-ticketing">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
                <Clock className="w-4 h-4" /> Latest ticketing
              </div>
              <span className="text-sm font-mono text-amber-900">{String(fr.latest_ticketing).replace("T", " ").slice(0, 16)}</span>
            </div>
          )}

          {/* B2C: no supplier name surfaced. Admins see supplier details on
              the admin booking detail page instead. */}
          <div className="text-[11px] text-slate-500 leading-relaxed">
            {isAmadeus
              ? "Exact change, cancellation and no-show amounts are governed by the carrier's published fare rules. Please contact the airline for the precise penalty."
              : "Penalty amounts are pulled live and may be re-validated against the carrier's published rules at time of ticketing."}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
